import { ArrowUpRight } from "lucide-react";
import { gridTrades, linePath, type Pt } from "features/onboarding/art/chart";
import {
  Art,
  ArtSwitch,
  Head,
  Line,
  Status,
  Toast,
  Window,
} from "features/onboarding/art/live";
import {
  TXT,
  fade,
  font,
  pop,
  r2,
  ride,
  rise,
  say,
  tone,
  useArtId,
  type Box,
  type Scene,
} from "features/onboarding/art/kit";
import useArtLabels from "features/onboarding/art/labels";

// Ported from cornix.io (feature/art/grid-bots.tsx): buys below the price,
// sells above, and every sell one grid level above its buy.

const LEVELS = 5;
const UPPER = 66200;
const LOWER = 58400;
const PROFIT = 7.1;

/** Sideways chop across the band (0 = upper price), starting on the middle level. */
const wave = (u: number) =>
  0.5 + 0.31 * Math.sin(11.3 * u) * (0.8 + 0.2 * Math.sin(4 * u + 0.5));

type Geo = {
  name: string;
  w: number;
  h: number;
  win: Box;
  chart: Box;
  band: Box;
  tagX: number;
  stats: Box[];
  big: boolean;
  toast: { x: number; y: number; w: number };
};

const WIDE: Geo = {
  name: "w",
  w: 640,
  h: 440,
  win: { x: 22, y: 20, w: 536, h: 392 },
  chart: { x: 42, y: 72, w: 366, h: 320 },
  band: { x: 52, y: 100, w: 292, h: 270 },
  tagX: 362,
  stats: [
    { x: 420, y: 72, w: 122, h: 82 },
    { x: 420, y: 166, w: 122, h: 82 },
    { x: 420, y: 260, w: 122, h: 82 },
  ],
  big: true,
  toast: { x: 420, y: 352, w: 212 },
};

const TALL: Geo = {
  name: "t",
  w: 360,
  h: 482,
  win: { x: 8, y: 8, w: 344, h: 392 },
  chart: { x: 22, y: 52, w: 316, h: 246 },
  band: { x: 26, y: 84, w: 226, h: 198 },
  tagX: 276,
  stats: [
    { x: 22, y: 314, w: 100, h: 68 },
    { x: 130, y: 314, w: 100, h: 68 },
    { x: 238, y: 314, w: 100, h: 68 },
  ],
  big: false,
  toast: { x: 68, y: 414, w: 224 },
};

export function GridBotsArt({ play }: { play?: boolean }) {
  return (
    <ArtSwitch
      wide={<GridBots g={WIDE} play={play} />}
      narrow={<GridBots g={TALL} play={play} />}
    />
  );
}

function GridBots({ g, play }: { g: Geo; play?: boolean }) {
  const L = useArtLabels();
  const id = useArtId(`grid-${g.name}`);
  const { band } = g;
  const levels = Array.from(
    { length: LEVELS },
    (_, i) => band.y + ((i + 1) * band.h) / (LEVELS + 1)
  );
  const pts: Pt[] = Array.from({ length: 161 }, (_, i) => {
    const u = i / 160;
    return [band.x + u * band.w, r2(band.y + wave(u) * band.h)];
  });
  const trades = gridTrades(pts, levels);
  const sells = trades.filter((t) => t.side === "sell").length;
  const end = pts[pts.length - 1];

  const START = 0.5;
  const DRAW = 6;
  const at = (f: number) => START + f * DRAW;

  const scene: Scene = (tl, $) => {
    ride(tl, $("price"), $("head")[0], pts, START, DRAW);
    let n = 0;
    let s = 0;
    trades.forEach((t, i) => {
      const time = at(t.f);
      pop(tl, $(`dot${i}`), time, 8.5);
      rise(tl, $(`dot-text${i}`), time + 0.05, 0, 0.3);
      tl.fromTo(
        $(`flash${i}`),
        { opacity: 0.9 },
        { opacity: 0, duration: 0.9, immediateRender: false },
        time
      );
      n++;
      if (t.side === "sell") s++;
      say(tl, $("trades")[0], String(n), time, "0");
      say(tl, $("profit")[0], `+$${(s * PROFIT).toFixed(2)}`, time, "+$0.00");
    });
    const lastSell = [...trades].reverse().find((t) => t.side === "sell");
    rise(
      tl,
      $("toast"),
      (lastSell ? at(lastSell.f) : START + DRAW) + 0.1,
      12,
      0.7
    );
    const rest = START + DRAW + 0.8;

    const out = rest + 3.6;
    fade(tl, [...$("price"), ...$("head"), ...$("dot*"), ...$("toast")], out);
    tl.to({}, { duration: 0.6 }, out + 0.5);
    return rest;
  };

  const valueStyle = font(g.big ? 18 : 15, 600);
  const stats = [
    {
      label: L.profit,
      k: "profit",
      value: `+$${(sells * PROFIT).toFixed(2)}`,
      up: true,
    },
    { label: L.trades, k: "trades", value: String(trades.length) },
    { label: L.grids, k: undefined, value: String(LEVELS) },
  ];
  const initial = (word: string) => word.charAt(0).toUpperCase();

  return (
    <Art id={id} w={g.w} h={g.h} scene={scene} play={play}>
      <Window
        id={id}
        x={g.win.x}
        y={g.win.y}
        w={g.win.w}
        h={g.win.h}
        title={`${L.gridBot} · BTC/USDT`}
      >
        <g data-in="0.15">
          <rect
            x={band.x - 8}
            y={band.y}
            width={band.w + 16}
            height={band.h}
            rx={6}
            fill="var(--viz-accent-faint)"
            stroke="var(--viz-accent-line)"
            strokeDasharray="4 4"
          />
          {levels.map((y) => (
            <line
              key={y}
              x1={band.x - 8}
              x2={band.x + band.w + 8}
              y1={y}
              y2={y}
              stroke="var(--viz-rule)"
            />
          ))}
          {(
            [
              [band.y, UPPER],
              [band.y + band.h, LOWER],
            ] as const
          ).map(([y, p]) => (
            <text
              key={p}
              x={g.tagX}
              y={y === band.y ? y + 12 : y - 4}
              className={`${TXT.ink2} ${TXT.num}`}
              style={font(11, 600)}
            >
              {p.toLocaleString("en-US")}
            </text>
          ))}
        </g>
        {trades.map((t, i) => (
          <line
            key={`f${i}`}
            data-k={`flash${i}`}
            x1={band.x - 8}
            x2={band.x + band.w + 8}
            y1={t.y}
            y2={t.y}
            stroke={tone(t.side === "buy" ? "up" : "accent")}
            strokeWidth={1.6}
            opacity={0}
          />
        ))}

        <Line k="price" d={linePath(pts)} w={2.2} />
        {trades.map((t, i) => {
          const buy = t.side === "buy";
          return (
            <g key={i}>
              <circle
                data-k={`dot${i}`}
                cx={t.x}
                cy={t.y}
                r={8.5}
                fill={tone(buy ? "up" : "accent")}
                stroke="var(--viz-card)"
                strokeWidth={1.5}
              />
              <text
                data-k={`dot-text${i}`}
                x={t.x}
                y={t.y + 3.5}
                textAnchor="middle"
                fill="var(--viz-on-solid)"
                style={{ ...font(9.5, 600), pointerEvents: "none" }}
              >
                {initial(buy ? L.buy : L.sell)}
              </text>
            </g>
          );
        })}
        <Head k="head" x={end[0]} y={end[1]} />

        {g.stats.map((b, i) => {
          const s = stats[i];
          return (
            <g key={s.label} data-in={0.2 + i * 0.08}>
              <rect
                x={b.x}
                y={b.y}
                width={b.w}
                height={b.h}
                rx={12}
                fill="none"
                stroke="var(--viz-rule)"
              />
              <text x={b.x + 12} y={b.y + 22} className={TXT.label}>
                {s.label}
              </text>
              <text
                data-k={s.k}
                x={b.x + 12}
                y={b.y + (g.big ? 56 : 48)}
                fill={s.up ? tone("up") : undefined}
                className={s.up ? TXT.num : `${TXT.ink} ${TXT.num}`}
                style={valueStyle}
              >
                {s.value}
              </text>
            </g>
          );
        })}
        <Status x={g.win.x + g.win.w - 18} y={g.win.y + 23} text={L.active} />
      </Window>
      <Toast
        id={id}
        k="toast"
        x={g.toast.x}
        y={g.toast.y}
        w={g.toast.w}
        icon={ArrowUpRight}
        title={`${L.sellFilled} · +$${PROFIT.toFixed(2)}`}
        t="up"
      />
    </Art>
  );
}
